import type { SupportedLocale, Translations, TranslationDict } from "@repo/core";
import { de } from "./packs/de";
import { fr } from "./packs/fr";
import { it } from "./packs/it";
import { pt } from "./packs/pt";
import { uiEn, uiEs } from "./uiKeys";
import { getBuildLocale, isLocaleLocked, LAUNCH_LOCALES, readDemoLangParam } from "./localeMeta";

/** English base — every key should exist here (fallback). */
const en: TranslationDict = {
  // Nav groups
  "nav.group.setup": "Set Up Once",
  "nav.group.daily": "Use Daily",
  "nav.group.where": "Where You Are",
  "nav.group.wealth": "Build Wealth",
  // Nav items
  "nav.dashboard": "Dashboard",
  "nav.monthly": "Monthly Budget",
  "nav.transactions": "Transactions",
  "nav.calendar": "Spending Calendar",
  "nav.rule503020": "50/30/20 Rule",
  "nav.networth": "Net Worth",
  "nav.debt": "Debt Payoff",
  "nav.sinking": "Sinking Funds",
  "nav.settings": "Settings",
  // Bottom nav
  "nav.bottom.home": "Home",
  "nav.bottom.log": "Log",
  "nav.bottom.budget": "Budget",
  "nav.bottom.debt": "Debt",
  "nav.bottom.wealth": "Wealth",

  // Common
  "common.add": "Add",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.close": "Close",
  "common.export": "Export",
  "common.all": "All",
  "common.person": "Person",
  "common.year": "Year",
  "common.loading": "Loading…",
  "common.newTransaction": "New Transaction",
  "common.uncategorized": "Uncategorized",
  "common.mainAccount": "Main Account",

  // Groups
  "group.income": "Income",
  "group.needs": "Needs",
  "group.wants": "Wants",
  "group.savings": "Savings",

  // Account types
  "accountType.checking": "Checking",
  "accountType.savings": "Savings",
  "accountType.asset": "Asset",
  "accountType.debt": "Debt",

  // Categories
  "cat.cat-sal": "Salary",
  "cat.cat-side": "Side Hustle",
  "cat.cat-div": "Dividends",
  "cat.cat-rent": "Rent / Mortgage",
  "cat.cat-util": "Utilities",
  "cat.cat-groc": "Groceries",
  "cat.cat-ins": "Insurance",
  "cat.cat-trans": "Transport",
  "cat.cat-health": "Health",
  "cat.cat-eat": "Dining Out",
  "cat.cat-ent": "Entertainment",
  "cat.cat-shop": "Shopping",
  "cat.cat-sub": "Subscriptions",
  "cat.cat-travel": "Travel",
  "cat.cat-emerg": "Emergency Fund",
  "cat.cat-invest": "Investments",
  "cat.cat-ret": "Retirement (401k)",

  // Demo accounts / debts / funds
  "demo.acc-check": "Chase Checking",
  "demo.acc-sav": "HYSA Savings",
  "demo.acc-cc1": "Visa Credit Card",
  "demo.acc-car": "Car Loan",
  "demo.acc-inv": "Brokerage",
  "demo.acc-401k": "401(k)",
  "demo.dbt-cc": "Visa Credit Card",
  "demo.dbt-car": "Car Loan",
  "demo.dbt-sl": "Student Loan",
  "demo.sf-hol": "Holiday Travel",
  "demo.sf-emg": "Emergency Fund",
  "demo.sf-car": "New Car Deposit",
  "demo.sf-home": "Home Repairs",
  "demo.sf-tech": "New Laptop",
  "demo.note.overDining": "Dinner with friends",
  "demo.note.workLunch": "Work lunch",
  "demo.note.weekendOut": "Weekend out",

  // Quick-Start: unzip before opening the app
  "qs.unzip.title": "Before you start: unzip the folder",
  "qs.unzip.windows":
    "Windows: right-click the .zip file -> \"Extract All\" -> choose where to save it.",
  "qs.unzip.mac":
    "Mac: double-click the .zip file - it unzips automatically next to the original zip.",
  "qs.unzip.open":
    "Open the folder that was created (same name as the zip). Inside you'll find index.html, this guide, and a folder called assets.",
  "qs.unzip.warn":
    "Don't move or rename index.html on its own, or take it out of that folder - it needs to stay next to the assets folder to work.",

  // Settings
  "settings.title": "Settings",
  "settings.subtitle": "Customize appearance, language, categories, and data.",
  "settings.language": "Language",
  "settings.languageSection": "Language & Region",
  "settings.datePreview": "Date preview",
  "settings.timePreview": "Time preview",
  "settings.currencyPreview": "Currency preview",
  "settings.relativePreview": "Relative time",
  "settings.categories": "Categories",
  "settings.accounts": "Accounts",
  "settings.restoreDefaults": "Restore Default Categories",
  "settings.restoreDefaultsConfirm":
    "Replace all categories with the default set (Groceries, Rent, etc.)? Custom categories will be removed.",
  "settings.addFirstAccount": "Add your first account to track checking, savings, debts, and assets.",
  "settings.loadDemo": "Load Demo Data",
  "settings.loadDemoDesc":
    "Clears all data and populates 12 months of realistic income, expenses, debts, and savings entries.",
  "settings.loadDemoBtn": "Load Demo Data",
  "settings.loadingDemo": "Loading…",
  "settings.clearAll": "Clear All Data",
  "settings.clearAllDesc":
    "Permanently deletes all transactions, accounts, debts, and sinking funds. Default categories are restored. This cannot be undone.",
  "settings.clearConfirm": "Yes, delete all",
  "settings.clearing": "Clearing…",
  "settings.clearedDone":
    "All personal data cleared. Default categories are ready — add an account when you want, then log transactions.",
  "settings.demoLoaded": "Demo data loaded! 12 months of transactions are ready.",
  "settings.calcCheck": "Calculation check",
  "settings.runVerification": "Run Verification Script",
  "settings.calcCheckDesc":
    "Confirms Dashboard, budget, calendar, and rule-of-thumb totals still add up correctly.",
  "settings.emptyAccounts": "Add your first account",
  "settings.categoryName": "Category name",
  "settings.accountName": "e.g. Chase Checking",

  // Help
  "help.title": "Help & FAQ",
  "help.close": "Close help",
  "help.replayTour": "Replay quick-start tour",
  "help.quickStart": "Open Quick-Start guide (PDF)",
  "help.quickStartHtml": "Open HTML version",
  "help.q0": "How does backup & restore work?",
  "help.a0":
    "Settings → Backup & Restore downloads a JSON file with every category, transaction, debt, and fund. Import that file on the same or another browser to restore totals exactly. Back up regularly — this file is your only portable copy.",
  "help.q1": "How do I move data to a new device or browser?",
  "help.a1":
    "Export a backup on the old device, open BudgetOS on the new one, then Import Backup. Do this before clearing site data or switching browsers.",
  "help.q2": "How do I reset all data?",
  "help.a2":
    "Settings → Clear All Data permanently deletes transactions, accounts, debts, and sinking funds, then restores the default categories so you can keep logging right away. Accounts are not recreated (add your own in Settings). Refresh will not resurrect demo data unless you tap Load Demo Data. To reset only the category list, use Categories → Restore Default Categories.",
  "help.q3": "How do I change currency or language?",
  "help.a3":
    "Settings → Currency for money formatting. Settings → Language & Region for the app language. Stored amounts stay the same.",
  "help.q4": "Does everything work offline?",
  "help.a4":
    "Yes. Once the HTML/app is loaded, BudgetOS needs no internet. Charts, debts, exports, and backups all run locally.",
  "help.q5": "Which browsers are supported?",
  "help.a5":
    "Current Chrome, Firefox, Edge, and Safari. Use a modern browser with IndexedDB enabled. Private/incognito modes may wipe data when the window closes.",
  "help.q6": "Important: don’t move the HTML file after saving data",
  "help.a6":
    "If you open BudgetOS via a file:// path (common with Etsy downloads), some browsers — especially Safari — scope storage to that file path. After you’ve saved data, export a backup before renaming or moving the file, then import again after the move.",
  "help.q7": "PDF export shows a URL or date in the margin — is that a bug?",
  "help.a7":
    "The Annual Report and section PDFs are generated offline with jsPDF (not the browser Print dialog), so they include BudgetOS headers, page numbers, and charts without relying on “Print backgrounds.” If you still use Print from the browser menu, you can turn off headers/footers in the print dialog.",

  // Onboarding
  "tour.skip": "Skip",
  "tour.back": "Back",
  "tour.next": "Next",
  "tour.done": "Get started",
  "tour.step": "Step {n} of {total}",
  "tour.choice.eyebrow": "Welcome",
  "tour.choice.title": "How do you want to start?",
  "tour.choice.body":
    "Choose once. You can load or clear demo data later in Settings.",
  "tour.choice.sample": "Explore with sample data",
  "tour.choice.sampleDesc": "Loads a realistic year of demo numbers so you can click around.",
  "tour.choice.empty": "Start with my own data",
  "tour.choice.emptyDesc": "Empty app — only default categories. Add your first transaction when ready.",
  "tour.s0.title": "Your data stays on this device",
  "tour.s0.body":
    "BudgetOS is offline-first. Everything is stored in this browser — no account, no cloud, no tracking.",
  "tour.s1.title": "Add a transaction anytime",
  "tour.s1.body":
    "Tap the + button (or open Transactions → Add) to log income or spending. Totals update instantly across Dashboard, Budget, and Calendar.",
  "tour.s2.title": "Settings & backups",
  "tour.s2.body":
    "Open Settings to change currency, theme, categories, and download a JSON backup. Export before switching browsers or devices.",
  "tour.s3.title": "Try demo data anytime",
  "tour.s3.body":
    "Settings → Load Demo Data fills a realistic year for exploration. Clear All Data removes it when you’re ready for your own numbers.",
  "tour.s4.title": "Help is always here",
  "tour.s4.body": "Re-open this tour or the FAQ anytime from the ? button. You’re ready to budget.",

  // Empty states
  "empty.accounts.networth": "Add your first account in Settings to see balances here.",
  "empty.debts":
    "No debts yet. Load demo data or add debts when you set up your accounts — this screen stays ready until then.",
  "empty.tx.noAccount":
    "No accounts yet — you can still save. Add your first account in Settings when ready.",
  "csv.needAccount":
    "Add your first account in Settings before importing — CSV rows need an account to attach to.",

  // Tx modal
  "tx.account": "Account",
  "tx.category": "Category",
  "tx.chooseAccount": "— Choose Account —",

  // Demo banner
  "demo.banner":
    "This is a live demo of BudgetOS — sample data, resets each visit.",
  "demo.readOnly":
    "This is a preview demo. Changes are not saved — get the full version on Etsy to keep your own budget.",
  "demo.cta": "Get the full version",
  "demo.ctaHint": "Set DEMO_ETSY_URL in this app’s src/demoConfig.ts.",

  // Dashboard
  "dash.yearGlance": "Year At A Glance — {year}",
  "dash.downloadReport": "Download Annual Report",
  "dash.buildingPdf": "Building PDF…",
  "dash.savingsGoal": "Savings Goal Progress",
  "dash.savingsRateTarget": "Savings Rate · Target 20%",
  "dash.rateVsTarget": "Rate vs 20% target",
  "dash.monthHighlights": "Month Highlights",
  "dash.kpi.moneyIn": "Money In",
  "dash.kpi.moneyOut": "Money Out",
  "dash.kpi.leftover": "Leftover",
  "dash.kpi.savingsRate": "Savings Rate",
  "dash.kpi.totalSaved": "Total Saved",

  // Net worth UI
  "nw.title": "Net Worth Tracker",
  "nw.logMonth": "Log Month",
  "nw.current": "Current Net Worth",
  "nw.vsLastMonth": "vs last month",
  "nw.assets": "Assets",
  "nw.debts": "Debts",
  "nw.debtRatio": "Debt Ratio",
  "nw.monthsTracked": "Months Tracked",
  "nw.logForm": "Log Net Worth",
  "nw.trend": "Net Worth Trend",
  "nw.accounts": "Accounts Breakdown",
  "nw.saveLog": "Save Log",
  "nw.netWorth": "Net Worth",

  // PDF report (static chrome / sections / columns)
  "pdf.tagline": "Offline annual planner",
  "pdf.cover.disclaimer":
    "Figures match on-screen BudgetOS calculations. Generated entirely offline.",
  "pdf.generated": "Generated {date}",
  "pdf.page": "Page {n} of {total}",
  "pdf.view": "View: {person}",
  "pdf.title.annual": "{year} Annual Report",
  "pdf.period.detail": "{year} Annual Report · detail {month}",
  "pdf.section.yearGlance": "Year at a Glance",
  "pdf.section.cashFlow": "Monthly Cash Flow",
  "pdf.section.budget": "Monthly Budget — {month} {year}",
  "pdf.section.rule": "50/30/20 Distribution",
  "pdf.section.debts": "Debt Payoff Summary",
  "pdf.section.funds": "Sinking Funds",
  "pdf.section.netWorth": "Net Worth",
  "pdf.col.kpi": "KPI",
  "pdf.col.value": "Value",
  "pdf.col.month": "Month",
  "pdf.col.in": "In",
  "pdf.col.out": "Out",
  "pdf.col.leftover": "Leftover",
  "pdf.col.category": "Category",
  "pdf.col.target": "Target",
  "pdf.col.actual": "Actual",
  "pdf.col.variance": "Variance",
  "pdf.col.group": "Group",
  "pdf.col.bucket": "Bucket",
  "pdf.col.amount": "Amount",
  "pdf.col.pctIncome": "% of income",
  "pdf.col.targetPct": "Target %",
  "pdf.col.debt": "Debt",
  "pdf.col.balance": "Balance",
  "pdf.col.apr": "APR",
  "pdf.col.interest": "Interest",
  "pdf.col.months": "Months",
  "pdf.col.payoff": "Payoff",
  "pdf.col.fund": "Fund",
  "pdf.col.saved": "Saved",
  "pdf.col.progress": "Progress",
  "pdf.col.moNeeded": "Mo. needed",
  "pdf.col.assets": "Assets",
  "pdf.col.debts": "Debts",
  "pdf.col.net": "Net",
  "pdf.col.account": "Account",
  "pdf.col.type": "Type",
  "pdf.col.payment": "Payment",
  "pdf.col.principal": "Principal",
  "pdf.sub.incomeSources": "Income sources",
  "pdf.sub.nws": "Needs / Wants / Savings",
  "pdf.sub.expenseCats": "Expense categories",
  "pdf.sub.accounts": "Accounts breakdown",
  "pdf.kpi.moneyIn": "Money In",
  "pdf.kpi.moneyOut": "Money Out",
  "pdf.kpi.leftover": "Leftover",
  "pdf.kpi.savingsRate": "Savings Rate",
  "pdf.kpi.totalSaved": "Total Saved",
  "pdf.savingsGoal": "Savings goal: {actual} / {target}",
  "pdf.debtSummary":
    "Total debt {debt} · Total interest {interest} · Debt-free {date}",
  "pdf.nwSummary": "Net {net} · Assets {assets} · Debts {debts}",
  "pdf.empty.debts": "No debts on file.",
  "pdf.empty.funds": "No sinking funds on file.",
  "pdf.empty.chart": "No chart available for this period.",
  "pdf.empty.rule": "No expense distribution in this period — actuals are $0.",
  "pdf.chart.cashFlow": "Monthly Cash Flow",
  "pdf.chart.income": "Income",
  "pdf.chart.expense": "Expense",
  "pdf.chart.rule": "50/30/20 · {month} {year}",
  "pdf.chart.netWorthTrend": "Net Worth Trend",
  "pdf.chart.netWorth": "Net Worth",
  "pdf.chart.assets": "Assets",
  "pdf.chart.debts": "Debts",
  "pdf.chart.emptySpend": "No spending in this period",
  "pdf.chart.emptyHint": "Needs / Wants / Savings will appear when this month has expenses.",
  "pdf.amortization": "Amortization — {name}",
  "pdf.payoffInterest": "Payoff {date} · Interest {interest}",
};

/** Spanish overrides (merged onto English). */
const es: TranslationDict = {
  "nav.group.setup": "Configura una vez",
  "nav.group.daily": "Uso diario",
  "nav.group.where": "Dónde estás",
  "nav.group.wealth": "Construye patrimonio",
  "nav.dashboard": "Panel",
  "nav.monthly": "Presupuesto mensual",
  "nav.transactions": "Movimientos",
  "nav.calendar": "Calendario de gastos",
  "nav.rule503020": "Regla 50/30/20",
  "nav.networth": "Patrimonio neto",
  "nav.debt": "Pagar deudas",
  "nav.sinking": "Fondos objetivo",
  "nav.settings": "Ajustes",
  "nav.bottom.home": "Inicio",
  "nav.bottom.log": "Registrar",
  "nav.bottom.budget": "Presupuesto",
  "nav.bottom.debt": "Deudas",
  "nav.bottom.wealth": "Patrimonio",

  "common.add": "Añadir",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.delete": "Eliminar",
  "common.edit": "Editar",
  "common.close": "Cerrar",
  "common.export": "Exportar",
  "common.all": "Todos",
  "common.person": "Persona",
  "common.year": "Año",
  "common.loading": "Cargando…",
  "common.newTransaction": "Nuevo movimiento",
  "common.uncategorized": "Sin categoría",
  "common.mainAccount": "Cuenta principal",

  "group.income": "Ingresos",
  "group.needs": "Necesidades",
  "group.wants": "Deseos",
  "group.savings": "Ahorro",

  "accountType.checking": "Corriente",
  "accountType.savings": "Ahorros",
  "accountType.asset": "Activo",
  "accountType.debt": "Deuda",

  "cat.cat-sal": "Salario",
  "cat.cat-side": "Ingresos extra",
  "cat.cat-div": "Dividendos",
  "cat.cat-rent": "Alquiler / Hipoteca",
  "cat.cat-util": "Servicios",
  "cat.cat-groc": "Comestibles",
  "cat.cat-ins": "Seguros",
  "cat.cat-trans": "Transporte",
  "cat.cat-health": "Salud",
  "cat.cat-eat": "Comer fuera",
  "cat.cat-ent": "Entretenimiento",
  "cat.cat-shop": "Compras",
  "cat.cat-sub": "Suscripciones",
  "cat.cat-travel": "Viajes",
  "cat.cat-emerg": "Fondo de emergencia",
  "cat.cat-invest": "Inversiones",
  "cat.cat-ret": "Jubilación (401k)",

  "demo.acc-check": "Cuenta corriente Chase",
  "demo.acc-sav": "Ahorros HYSA",
  "demo.acc-cc1": "Tarjeta Visa",
  "demo.acc-car": "Préstamo del coche",
  "demo.acc-inv": "Brokerage",
  "demo.acc-401k": "401(k)",
  "demo.dbt-cc": "Tarjeta Visa",
  "demo.dbt-car": "Préstamo del coche",
  "demo.dbt-sl": "Préstamo estudiantil",
  "demo.sf-hol": "Viaje de vacaciones",
  "demo.sf-emg": "Fondo de emergencia",
  "demo.sf-car": "Entrada coche nuevo",
  "demo.sf-home": "Reparaciones del hogar",
  "demo.sf-tech": "Portátil nuevo",
  "demo.note.overDining": "Cena con amigos",
  "demo.note.workLunch": "Almuerzo de trabajo",
  "demo.note.weekendOut": "Salida del fin de semana",

  "qs.unzip.title": "Antes de empezar: descomprime la carpeta",
  "qs.unzip.windows":
    "Windows: clic derecho sobre el archivo .zip -> \"Extraer todo\" -> elige donde guardarlo.",
  "qs.unzip.mac":
    "Mac: doble clic sobre el archivo .zip - se descomprime solo, al lado del zip original.",
  "qs.unzip.open":
    "Abre la carpeta que se creó (tiene el mismo nombre que el zip). Ahí adentro está index.html, esta guía, y una carpeta llamada assets.",
  "qs.unzip.warn":
    "No muevas ni renombres index.html por separado, ni lo saques de esa carpeta - necesita estar junto a la carpeta assets para funcionar.",

  "settings.title": "Ajustes",
  "settings.subtitle": "Personaliza apariencia, idioma, categorías y datos.",
  "settings.language": "Idioma",
  "settings.languageSection": "Idioma y región",
  "settings.datePreview": "Vista previa de fecha",
  "settings.timePreview": "Vista previa de hora",
  "settings.currencyPreview": "Vista previa de moneda",
  "settings.relativePreview": "Tiempo relativo",
  "settings.categories": "Categorías",
  "settings.accounts": "Cuentas",
  "settings.restoreDefaults": "Restaurar categorías por defecto",
  "settings.restoreDefaultsConfirm":
    "¿Sustituir todas las categorías por el conjunto por defecto (Comestibles, Alquiler, etc.)? Se eliminarán las personalizadas.",
  "settings.addFirstAccount":
    "Añade tu primera cuenta para llevar corriente, ahorros, deudas y activos.",
  "settings.loadDemo": "Cargar datos de demo",
  "settings.loadDemoDesc":
    "Borra todos los datos y rellena 12 meses de ingresos, gastos, deudas y ahorro de ejemplo.",
  "settings.loadDemoBtn": "Cargar datos de demo",
  "settings.loadingDemo": "Cargando…",
  "settings.clearAll": "Borrar todos los datos",
  "settings.clearAllDesc":
    "Elimina permanentemente movimientos, cuentas, deudas y fondos objetivo. Se restauran las categorías por defecto. No se puede deshacer.",
  "settings.clearConfirm": "Sí, borrar todo",
  "settings.clearing": "Borrando…",
  "settings.clearedDone":
    "Datos personales borrados. Las categorías por defecto están listas — añade una cuenta cuando quieras y registra movimientos.",
  "settings.demoLoaded": "¡Demo cargada! Ya tienes 12 meses de movimientos.",
  "settings.calcCheck": "Comprobación de cálculos",
  "settings.runVerification": "Ejecutar verificación",
  "settings.calcCheckDesc":
    "Confirma que Dashboard, presupuesto, calendario y la regla 50/30/20 cuadran.",
  "settings.emptyAccounts": "Añade tu primera cuenta",
  "settings.categoryName": "Nombre de categoría",
  "settings.accountName": "p. ej. Cuenta corriente",

  "help.title": "Ayuda y FAQ",
  "help.close": "Cerrar ayuda",
  "help.replayTour": "Repetir tour de inicio",
  "help.quickStart": "Abrir guía de inicio rápido (PDF)",
  "help.quickStartHtml": "Abrir versión HTML",
  "help.q0": "¿Cómo funcionan la copia y la restauración?",
  "help.a0":
    "Ajustes → Copia de seguridad descarga un JSON con categorías, movimientos, deudas y fondos. Impórtalo en el mismo u otro navegador para restaurar. Haz copias a menudo: ese archivo es tu única copia portable.",
  "help.q1": "¿Cómo paso los datos a otro dispositivo o navegador?",
  "help.a1":
    "Exporta una copia en el dispositivo antiguo, abre BudgetOS en el nuevo e Importa. Hazlo antes de borrar datos del sitio o cambiar de navegador.",
  "help.q2": "¿Cómo restablezco todos los datos?",
  "help.a2":
    "Ajustes → Borrar todos los datos elimina movimientos, cuentas, deudas y fondos, y restaura las categorías por defecto. Las cuentas no se recrean. Un refresco no trae la demo salvo que pulses Cargar datos de demo. Solo categorías: Restaurar categorías por defecto.",
  "help.q3": "¿Cómo cambio la moneda o el idioma?",
  "help.a3":
    "Ajustes → Moneda para el formato. Ajustes → Idioma y región para el idioma de la app. Los importes guardados no cambian.",
  "help.q4": "¿Funciona todo sin conexión?",
  "help.a4":
    "Sí. Una vez cargada la app, BudgetOS no necesita internet. Gráficos, deudas, exportaciones y copias van en local.",
  "help.q5": "¿Qué navegadores están soportados?",
  "help.a5":
    "Chrome, Firefox, Edge y Safari actuales. Usa un navegador moderno con IndexedDB. El modo privado puede borrar datos al cerrar.",
  "help.q6": "Importante: no muevas el HTML después de guardar datos",
  "help.a6":
    "Si abres BudgetOS con file:// (típico en descargas de Etsy), algunos navegadores —sobre todo Safari— asocian el almacenamiento a esa ruta. Exporta una copia antes de renombrar o mover el archivo e importa después.",
  "help.q7": "El PDF muestra una URL o fecha en el margen — ¿es un bug?",
  "help.a7":
    "El informe anual y los PDF por sección se generan offline con jsPDF (no el diálogo Imprimir del navegador), con cabeceras BudgetOS, números de página y gráficos. Si usas Imprimir del navegador, puedes desactivar encabezados/pies en el diálogo.",

  "tour.skip": "Omitir",
  "tour.back": "Atrás",
  "tour.next": "Siguiente",
  "tour.done": "Empezar",
  "tour.step": "Paso {n} de {total}",
  "tour.choice.eyebrow": "Bienvenida",
  "tour.choice.title": "¿Cómo quieres empezar?",
  "tour.choice.body":
    "Elige una vez. Luego puedes cargar o borrar la demo en Ajustes.",
  "tour.choice.sample": "Explorar con datos de ejemplo",
  "tour.choice.sampleDesc": "Carga un año realista de demo para recorrer la app.",
  "tour.choice.empty": "Empezar con mis datos",
  "tour.choice.emptyDesc": "App vacía — solo categorías por defecto. Tu primer paso será añadir un movimiento.",
  "tour.s0.title": "Tus datos se quedan en este dispositivo",
  "tour.s0.body":
    "BudgetOS es offline-first. Todo se guarda en este navegador — sin cuenta, sin nube, sin tracking.",
  "tour.s1.title": "Añade un movimiento cuando quieras",
  "tour.s1.body":
    "Pulsa + (o Movimientos → Añadir) para registrar ingresos o gastos. Los totales se actualizan al instante.",
  "tour.s2.title": "Ajustes y copias",
  "tour.s2.body":
    "En Ajustes cambias moneda, tema, categorías y descargas una copia JSON. Exporta antes de cambiar de navegador.",
  "tour.s3.title": "Prueba la demo cuando quieras",
  "tour.s3.body":
    "Ajustes → Cargar datos de demo rellena un año de ejemplo. Borrar todos los datos la quita cuando quieras usar los tuyos.",
  "tour.s4.title": "La ayuda siempre está aquí",
  "tour.s4.body": "Reabre este tour o el FAQ desde el botón ?. Ya puedes presupuestar.",

  "empty.accounts.networth": "Añade tu primera cuenta en Ajustes para ver saldos aquí.",
  "empty.debts":
    "Aún no hay deudas. Carga la demo o añade deudas al configurar cuentas — esta pantalla espera lista.",
  "empty.tx.noAccount":
    "Aún no hay cuentas — puedes guardar igual. Añade tu primera cuenta en Ajustes cuando quieras.",
  "csv.needAccount":
    "Añade tu primera cuenta en Ajustes antes de importar — las filas CSV necesitan una cuenta.",

  "tx.account": "Cuenta",
  "tx.category": "Categoría",
  "tx.chooseAccount": "— Elegir cuenta —",

  "demo.banner":
    "Esta es una demo en vivo de BudgetOS — datos de ejemplo, se reinicia en cada visita.",
  "demo.readOnly":
    "Esta es una demo de prueba. Los cambios no se guardan — consigue la versión completa en Etsy para tu presupuesto.",
  "demo.cta": "Obtener la versión completa",
  "demo.ctaHint": "Define DEMO_ETSY_URL en el src/demoConfig.ts de esta app.",

  "dash.yearGlance": "El año de un vistazo — {year}",
  "dash.downloadReport": "Descargar informe anual",
  "dash.buildingPdf": "Generando PDF…",
  "dash.savingsGoal": "Progreso de la meta de ahorro",
  "dash.savingsRateTarget": "Tasa de ahorro · Objetivo 20%",
  "dash.rateVsTarget": "Tasa vs objetivo 20%",
  "dash.monthHighlights": "Destacados del mes",
  "dash.kpi.moneyIn": "Dinero entrante",
  "dash.kpi.moneyOut": "Dinero saliente",
  "dash.kpi.leftover": "Sobrante",
  "dash.kpi.savingsRate": "Tasa de ahorro",
  "dash.kpi.totalSaved": "Total ahorrado",

  "nw.title": "Seguimiento del patrimonio neto",
  "nw.logMonth": "Registrar mes",
  "nw.current": "Patrimonio neto actual",
  "nw.vsLastMonth": "vs mes anterior",
  "nw.assets": "Activos",
  "nw.debts": "Deudas",
  "nw.debtRatio": "Ratio de deuda",
  "nw.monthsTracked": "Meses registrados",
  "nw.logForm": "Registrar patrimonio neto",
  "nw.trend": "Tendencia del patrimonio neto",
  "nw.accounts": "Desglose de cuentas",
  "nw.saveLog": "Guardar registro",
  "nw.netWorth": "Patrimonio neto",

  "pdf.tagline": "Planificador anual offline",
  "pdf.cover.disclaimer":
    "Las cifras coinciden con los cálculos de BudgetOS en pantalla. Generado totalmente sin conexión.",
  "pdf.generated": "Generado {date}",
  "pdf.page": "Página {n} de {total}",
  "pdf.view": "Vista: {person}",
  "pdf.title.annual": "Informe anual {year}",
  "pdf.period.detail": "Informe anual {year} · detalle {month}",
  "pdf.section.yearGlance": "El año de un vistazo",
  "pdf.section.cashFlow": "Flujo de caja mensual",
  "pdf.section.budget": "Presupuesto mensual — {month} {year}",
  "pdf.section.rule": "Distribución 50/30/20",
  "pdf.section.debts": "Resumen de deudas",
  "pdf.section.funds": "Fondos objetivo",
  "pdf.section.netWorth": "Patrimonio neto",
  "pdf.col.kpi": "KPI",
  "pdf.col.value": "Valor",
  "pdf.col.month": "Mes",
  "pdf.col.in": "Entrada",
  "pdf.col.out": "Salida",
  "pdf.col.leftover": "Sobrante",
  "pdf.col.category": "Categoría",
  "pdf.col.target": "Objetivo",
  "pdf.col.actual": "Real",
  "pdf.col.variance": "Variación",
  "pdf.col.group": "Grupo",
  "pdf.col.bucket": "Cubo",
  "pdf.col.amount": "Importe",
  "pdf.col.pctIncome": "% del ingreso",
  "pdf.col.targetPct": "% objetivo",
  "pdf.col.debt": "Deuda",
  "pdf.col.balance": "Saldo",
  "pdf.col.apr": "TIN",
  "pdf.col.interest": "Intereses",
  "pdf.col.months": "Meses",
  "pdf.col.payoff": "Liquidación",
  "pdf.col.fund": "Fondo",
  "pdf.col.saved": "Ahorrado",
  "pdf.col.progress": "Progreso",
  "pdf.col.moNeeded": "Necesario/mes",
  "pdf.col.assets": "Activos",
  "pdf.col.debts": "Deudas",
  "pdf.col.net": "Neto",
  "pdf.col.account": "Cuenta",
  "pdf.col.type": "Tipo",
  "pdf.col.payment": "Pago",
  "pdf.col.principal": "Capital",
  "pdf.sub.incomeSources": "Fuentes de ingresos",
  "pdf.sub.nws": "Necesidades / Deseos / Ahorros",
  "pdf.sub.expenseCats": "Categorías de gasto",
  "pdf.sub.accounts": "Desglose de cuentas",
  "pdf.kpi.moneyIn": "Dinero entrante",
  "pdf.kpi.moneyOut": "Dinero saliente",
  "pdf.kpi.leftover": "Sobrante",
  "pdf.kpi.savingsRate": "Tasa de ahorro",
  "pdf.kpi.totalSaved": "Total ahorrado",
  "pdf.savingsGoal": "Meta de ahorro: {actual} / {target}",
  "pdf.debtSummary":
    "Deuda total {debt} · Intereses totales {interest} · Libre de deudas {date}",
  "pdf.nwSummary": "Neto {net} · Activos {assets} · Deudas {debts}",
  "pdf.empty.debts": "No hay deudas registradas.",
  "pdf.empty.funds": "No hay fondos objetivo registrados.",
  "pdf.empty.chart": "No hay gráfico disponible para este periodo.",
  "pdf.empty.rule": "Sin distribución de gastos en este periodo — los reales son 0.",
  "pdf.chart.cashFlow": "Flujo de caja mensual",
  "pdf.chart.income": "Ingresos",
  "pdf.chart.expense": "Gastos",
  "pdf.chart.rule": "50/30/20 · {month} {year}",
  "pdf.chart.netWorthTrend": "Tendencia del patrimonio neto",
  "pdf.chart.netWorth": "Patrimonio neto",
  "pdf.chart.assets": "Activos",
  "pdf.chart.debts": "Deudas",
  "pdf.chart.emptySpend": "Sin gastos en este periodo",
  "pdf.chart.emptyHint":
    "Necesidades / Deseos / Ahorros aparecerán cuando este mes tenga gastos.",
  "pdf.amortization": "Amortización — {name}",
  "pdf.payoffInterest": "Liquidación {date} · Intereses {interest}",
};

const LOCALES: SupportedLocale[] = [...LAUNCH_LOCALES];

const englishBase: TranslationDict = { ...en, ...uiEn };

/** Full dictionaries for the six Latin-script locales. */
export const translations: Translations = {
  en: englishBase,
  es: { ...englishBase, ...es, ...uiEs },
  fr: { ...englishBase, ...fr },
  de: { ...englishBase, ...de },
  pt: { ...englishBase, ...pt },
  it: { ...englishBase, ...it },
};

export {
  LAUNCH_LOCALES,
  isLaunchLocale,
  isLocaleLocked,
  getBuildLocale,
  readDemoLangParam,
  isHostedDemoBuild,
  UI_LOCALE_TAGS,
  uiLocaleTag,
} from "./localeMeta";

export const LOCALE_STORAGE_KEY = "annual-budget-locale";

export function readStoredLocale(): SupportedLocale {
  const locked = getBuildLocale();
  // Etsy customer zip: fixed language baked at build time
  if (locked && import.meta.env.VITE_DEMO_MODE !== "true") {
    return locked;
  }
  if (typeof window === "undefined") return "en";

  // Hosted demo (single deploy): language from ?lang= — default English
  if (
    import.meta.env.VITE_DEMO_MODE === "true" ||
    new URLSearchParams(window.location.search).has("demo")
  ) {
    return readDemoLangParam();
  }

  const raw = localStorage.getItem(LOCALE_STORAGE_KEY) || "en";
  return LOCALES.includes(raw as SupportedLocale) ? (raw as SupportedLocale) : "en";
}

export function writeStoredLocale(locale: SupportedLocale): void {
  if (isLocaleLocked()) return;
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export const ENGLISH_STRINGS: TranslationDict = { ...en, ...uiEn };
